import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
const AuthenticationPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    signIn,
    signUp,
    signInWithGoogle
  } = useAuth();
  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignUp) {
        // Sign up new user with additional metadata
        await signUp(email, password, {
          full_name: fullName,
          username: username
        });
      } else {
        // Sign in existing user
        await signIn(email, password);
      }
    } catch (err) {
      console.error("Authentication error:", err);
      // Toast shown by the auth context
    } finally {
      setLoading(false);
    }
  };
  const handleSocialLogin = async () => {
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error("Google login error:", err);
      // Toast shown by the auth context
    }
  };
  return <div className="max-w-md mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-slate-950 font-medium text-lg">
          {isSignUp ? (
            <>
              <button 
                type="button" 
                onClick={() => setIsSignUp(false)} 
                className="text-emerald-600 hover:text-emerald-500 underline font-medium"
              >
                Login
              </button> or <span className="text-black">Create an account</span>
            </>
          ) : (
            <>
              Login or <button 
                type="button" 
                onClick={() => setIsSignUp(true)} 
                className="text-emerald-600 hover:text-emerald-500 underline font-medium"
              >
                Create an account
              </button>
            </>
          )}
        </h2>
      </div>

      <Card className="border-0 shadow-none">
        <CardContent className="p-4">
          <form onSubmit={handleEmailAuth} className="space-y-3">
            {isSignUp && (
              <>
                <div className="relative">
                  <User className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                  <Input 
                    id="fullName" 
                    type="text" 
                    value={fullName} 
                    onChange={e => setFullName(e.target.value)} 
                    placeholder="Full Name" 
                    className="h-12 pl-10" 
                    required 
                  />
                </div>
                
                <div className="relative">
                  <User className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                  <Input 
                    id="username" 
                    type="text" 
                    value={username} 
                    onChange={e => setUsername(e.target.value)} 
                    placeholder="Username (Optional)" 
                    className="h-12 pl-10" 
                  />
                </div>
              </>
            )}
            
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
              <Input 
                id="email" 
                type="email" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                placeholder="Email Address" 
                className="h-12 pl-10" 
                required 
              />
            </div>
            
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
              <Input 
                id="password" 
                type={showPassword ? "text" : "password"}
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                placeholder="Password" 
                className="h-12 pl-10 pr-10" 
                required 
                minLength={isSignUp ? 6 : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3.5 h-4 w-4 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            
            <Button type="submit" disabled={loading} className="w-full h-12 text-white bg-slate-900 hover:bg-slate-800">
              {loading ? 'Processing...' : isSignUp ? 'Create Account' : 'Login'}
            </Button>
          </form>
          
          {!isSignUp && (
            <div className="text-center mt-4">
              <Link to="/forgot-password">
                <Button variant="link" className="text-slate-600 text-sm">
                  Forgot Your Password?
                </Button>
              </Link>
            </div>
          )}
          
          <div className="text-center mt-4">
            <Button variant="link" onClick={() => setIsSignUp(!isSignUp)} type="button" className="text-slate-950 text-sm">
              {isSignUp ? '← Back' : 'New here? Create an account'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 text-center text-xs text-gray-500">
        <p>
          By signing in, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>;
};
export default AuthenticationPage;